import {
  Box,
  Stack,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Toolbar,
  OutlinedInput,
  InputAdornment,
  Select,
  MenuItem,
  LinearProgress,
  SelectChangeEvent,
  CircularProgress,
  IconButton,
  useTheme
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ExtractParams } from '@/types/params';
// import { Payment } from '@/types/payment';

import { useGetPayments } from '@/api/endpoints/payment/useGetPayments';
import { useGetStripePaymentHistory } from '@/api/endpoints/payment/useGetStripePaymentHistory';
import { useGetStripeSubscriptions } from '@/api/endpoints/payment/useGetStripeSubscriptions';
import { ChevronLeftIcon } from '@/assets/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/assets/icons/ChevronRightIcon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { PMPRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';
import { formatFullDate } from '@/utils/date';

import RentSubscriptionCard from '@/components/resident/RentSubscriptionCard';

const TABLE_HEAD = [
  { id: 'period', label: 'Rent Period', sortable: true, width: '20%' },
  { id: 'amount', label: 'Amount', sortable: true, width: '15%' },
  { id: 'date', label: 'Payment Date', sortable: true, width: '20%' },
  { id: 'status', label: 'Rent Status', sortable: true, width: '15%' },
  { id: 'description', label: 'Description', sortable: false, width: '40%' },
];

type PaymentSortableField = 'period' | 'amount' | 'date' | 'status';

// Utility function to format currency values
const formatCurrency = (amount: number, isInCents = false): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  })
    .format(amount / (isInCents ? 100 : 1))
    .replace('$', '');

const TenantViewPaymentListSubpage: React.FC = () => {
  const { id } = useParams<ExtractParams<PMPRoutes.tenantViewPaymentList>>();
  const { session } = useAuth();
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<PaymentSortableField>('date');
  const [filterName, setFilterName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const theme = useTheme();

  const handleRequestSort = (property: PaymentSortableField) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  
  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };
  
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Get payments from Stripe including pending transactions
  const { data: stripePayments = [], isLoading: isStripePaymentsLoading } =
    useGetStripePaymentHistory(
      {
        tenant_id: id,
      },
      !!session?.access_token
    );

  // Fallback to database payments if Stripe API fails
  const { data: dbPayments = [], isLoading: isDbPaymentsLoading } = useGetPayments(
    {
      tenant_id: id,
    },
    !!session?.access_token && stripePayments.length === 0 && !isStripePaymentsLoading
  );

  // Get Stripe subscriptions for this tenant
  const stripeSubscriptionsParams = id ? { tenant_id: id } : undefined;
  const { data: stripeSubscriptions = [], isLoading: isSubscriptionsLoading } =
    useGetStripeSubscriptions(stripeSubscriptionsParams, !!session?.access_token);

  // Determine which payments source to use
  const payments = stripePayments.length > 0 ? stripePayments : dbPayments;

  // Find the active subscription if it exists
  const activeSubscription = useMemo(() => {
    const stripeSubscription = stripeSubscriptions.find(sub => sub.status === 'active');
    if (stripeSubscription) return stripeSubscription;

    return payments.find(
      payment =>
        payment.status === 'active' && payment.type === 'rent' && payment.isSubscription === true
    );
  }, [payments, stripeSubscriptions]);

  const isLoading = isStripePaymentsLoading || isDbPaymentsLoading || isSubscriptionsLoading;

  const handleSubscriptionCancelled = useCallback(() => {
    // No-op: Data will refresh due to react-query cache invalidation
  }, []);

  // Helper function to get color based on status
  const getStatusColor = (status: string): string => {
    if (status === 'completed' || status === 'active') {
      return 'success.main';
    }
    if (status === 'pending') {
      return 'warning.main';
    }
    return 'error.main';
  };

  const sortedPayments = useMemo(() => {
    if (!payments || payments.length === 0) return [];

    return [...payments].sort((a, b) => {
      if (orderBy === 'amount') {
        return order === 'asc' ? a.amount - b.amount : b.amount - a.amount;
      }

      if (orderBy === 'date') {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return order === 'asc' ? dateA - dateB : dateB - dateA;
      }

      if (orderBy === 'status') {
        return order === 'asc' 
          ? a.status.localeCompare(b.status)
          : b.status.localeCompare(a.status);
      }

      // For period and other string fields
      const aVal = a[orderBy] || '';
      const bVal = b[orderBy] || '';
      return order === 'asc' 
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [payments, order, orderBy]);

  const pagedPayments = useMemo(
    () => sortedPayments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedPayments, page, rowsPerPage]
  );

  if (isLoading && !payments.length && !stripeSubscriptions.length) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={TABLE_HEAD.length}>
            <LinearProgress />
          </TableCell>
        </TableRow>
      );
    }

    if (pagedPayments.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={TABLE_HEAD.length} align="center">
            No payments found
          </TableCell>
        </TableRow>
      );
    }

    return pagedPayments.map((row) => (
      <TableRow 
        key={row.id}
        sx={{
          bgcolor: theme.palette.secondary.contrastText,
          '&:hover': {
            backgroundColor: `${theme.palette.secondary[200]} !important`,
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
          },
        }}
      >
        <TableCell>{row.period || '-'}</TableCell>
        <TableCell>${formatCurrency(row.amount)}</TableCell>
        <TableCell>{formatFullDate(new Date(row.date))}</TableCell>
        <TableCell sx={{ color: getStatusColor(row.status) }}>
          {row.status}
        </TableCell>
        <TableCell>{row.description || '-'}</TableCell>
      </TableRow>
    ));
  };

  return (
    <Stack direction="column" spacing={2}>
      <Typography variant="body2">View this tenant's rent payment history.</Typography>
      {activeSubscription && (
        <RentSubscriptionCard
          activeSubscription={activeSubscription}
          onCancelled={handleSubscriptionCancelled}
          isStripeSubscription={stripeSubscriptions.some(sub => sub.id === activeSubscription.id)}
          tenantId={id}
          isPropertyManager
        />
      )}
      <Box sx={{ width: '100%', minHeight: 200 }}>
        <Card sx={{ bgcolor: 'common.white' }}>
          <Toolbar
            sx={{
              height: 64,
              display: 'flex',
              justifyContent: 'space-between',
              p: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'common.white',
            }}
          >
            <OutlinedInput
              fullWidth
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder="Search payments..."
              startAdornment={
                <InputAdornment position="start">
                  <SearchMdIcon sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              }
              sx={{
                maxWidth: 320,
                bgcolor: 'common.white',
              }}
            />
          </Toolbar>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {TABLE_HEAD.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      align="left"
                      sortDirection={orderBy === headCell.id ? order : false}
                      sx={{
                        width: headCell.width,
                        borderRight: 'none',
                        '&:last-child': {
                          borderRight: 'none',
                        },
                        bgcolor: 'secondary.200',
                        textTransform: 'none',
                        color: 'text.primary',
                        '& .MuiTableSortLabel-root': {
                          textTransform: 'none',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: { xs: '0.875rem', sm: 'inherit' },
                          fontWeight: 'bold',
                          color: 'text.primary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                          '&.Mui-active': {
                            color: 'text.primary',
                            '&:hover': {
                              color: 'text.primary',
                            },
                          },
                        },
                      }}
                    >
                      {headCell.sortable ? (
                        <TableSortLabel
                          active={orderBy === headCell.id}
                          direction={orderBy === headCell.id ? order : 'asc'}
                          onClick={() => handleRequestSort(headCell.id as PaymentSortableField)}
                        >
                          {headCell.label}
                        </TableSortLabel>
                      ) : (
                        <Typography
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: { xs: '0.875rem', sm: 'inherit' },
                            fontWeight: 'bold',
                            color: 'text.primary',
                            '&:hover': {
                              color: 'text.primary',
                            },
                          }}
                        >
                          {headCell.label}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {renderTableContent()}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'common.white',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Rows per page:
              </Typography>
              <Select
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                size="small"
                sx={{ width: 80 }}
                inputProps={{ 'aria-label': 'rows per page' }}
              >
                {[5, 25, 50, 100].map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {`${page * rowsPerPage + 1}-${Math.min(
                  (page + 1) * rowsPerPage,
                  sortedPayments.length
                )} of ${sortedPayments.length}`}
              </Typography>
              <IconButton
                onClick={() => handleChangePage(null, page - 1)}
                disabled={page === 0}
                size="small"
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={() => handleChangePage(null, page + 1)}
                disabled={page >= Math.ceil(sortedPayments.length / rowsPerPage) - 1}
                size="small"
              >
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          </Box>
        </Card>
      </Box>
    </Stack>
  );
};

export default TenantViewPaymentListSubpage;
